import { api } from "@/services/api"
import type { MerchantCurrencyCode } from "@/utils/currency"
import { useQuery } from "@tanstack/vue-query"

type PlatformSummaryTimeFrame =
  | "today"
  | "thisTimeLastWeek"
  | "thisWeek"
  | "lastWeek"
  | "thisMonth"
  | "lastMonth"

type PlatformSummaryCardData = Record<
  PlatformSummaryTimeFrame,
  number
>

export type CurrencyStats = {
  cardRevenue: PlatformSummaryCardData
  clientsTransacting: PlatformSummaryCardData
  newClients: PlatformSummaryCardData
}

type GetPlatformSummaryResponse = {
  data: Record<MerchantCurrencyCode, CurrencyStats>
  generatedAt: Date
}

const PLATFORM_SUMMARY_TPPLUS_QUERY_KEY =
  "platform_summary_tpplus"
const PLATFORM_SUMMARY_BEEPAID_GO_QUERY_KEY =
  "platform_summary_beepaid_go"

export const retrieveTpplusPlatformSummary =
  async (): Promise<GetPlatformSummaryResponse> => {
    const { body } = await api.get<
      RecursivelyConvertDatesToStrings<GetPlatformSummaryResponse>
    >("/platform_summary/tpplus")

    return {
      data: body.data,
      generatedAt: DateFns.parseISO(body.generatedAt),
    }
  }

export const retrieveBeepaidGoPlatformSummary =
  async (): Promise<GetPlatformSummaryResponse> => {
    const { body } = await api.get<
      RecursivelyConvertDatesToStrings<GetPlatformSummaryResponse>
    >("/platform_summary/beepaid_go")

    return {
      data: body.data,
      generatedAt: DateFns.parseISO(body.generatedAt),
    }
  }

export const useTpplusPlatformSummary = () => {
  const query = useQuery({
    queryKey: [PLATFORM_SUMMARY_TPPLUS_QUERY_KEY],
    queryFn: () => retrieveTpplusPlatformSummary(),
    // refresh every 5.5 minutes.
    // in production the data is regenerated every 5 minutes by django q
    staleTime: 330000,
    retry: 2,
  })

  return { ...query }
}

export const useBeepaidGoPlatformSummary = () => {
  const query = useQuery({
    queryKey: [PLATFORM_SUMMARY_BEEPAID_GO_QUERY_KEY],
    queryFn: () => retrieveBeepaidGoPlatformSummary(),
    // refresh every 5.5 minutes.
    // in production the data is regenerated every 5 minutes by django q
    staleTime: 330000,
    retry: 2,
  })

  return { ...query }
}
